import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { apiService } from '@/services/api'
import { resolveAppPath } from '@/utils/publicPath'
import { STORAGE_KEYS } from '@/utils/storageKeys'
import { getAccessToken } from '@/utils/authTokens'

// Layout components
const DashboardLayout = () => import('@/layouts/DashboardLayout.vue')
const MinimalLayout = () => import('@/layouts/MinimalLayout.vue')
// Auth: import estático — evita chunk lazy em 404 (ecrã preto se /assets/AuthLayout-*.js falhar)
import AuthLayout from '@/layouts/AuthLayout.vue'

// Page components
const Dashboard = () => import('@/views/Dashboard.vue')
import Login from '@/views/auth/Login.vue'
import LicenseInvalid from '@/views/LicenseInvalid.vue'
const InstallView = () => import('@/views/InstallView.vue')
const Servers = () => import('@/views/servers/Servers.vue')
// UsersManagement.vue removido - não é usado (Accounts.vue é a tela real)
const Categories = () => import('@/views/categories/Categories.vue')
const Resellers = () => import('@/views/reseller-management/Resellers.vue')
const Profile = () => import('@/views/Profile.vue')
const CheckUser = () => import('@/views/settings/CheckUser.vue')
const SystemLogs = () => import('@/views/settings/SystemLogs.vue')
const NotFound = () => import('@/views/NotFound.vue')

const routes = [
  // Rotas públicas / standalone (rodapé via MinimalLayout)
  {
    path: '/install',
    component: MinimalLayout,
    meta: { public: true },
    children: [
      {
        path: '',
        name: 'install',
        component: InstallView,
        meta: {
          title: 'Instalação - Veltrix Panel',
          public: true
        }
      }
    ]
  },
  {
    path: '/license-invalid',
    component: MinimalLayout,
    meta: { public: true },
    children: [
      {
        path: '',
        name: 'license-invalid',
        component: LicenseInvalid,
        meta: {
          title: 'Licença Inválida - Veltrix Panel',
          public: true
        }
      }
    ]
  },
  {
    path: '/sales/:slug',
    component: MinimalLayout,
    meta: { public: true },
    children: [
      {
        path: '',
        name: 'public-sales',
        component: () => import('@/views/public/AccountSales.vue'),
        meta: {
          title: 'Comprar Acesso - Veltrix Panel',
          public: true
        }
      }
    ]
  },
  {
    path: '/reseller-sales/:slug',
    component: MinimalLayout,
    meta: { public: true },
    children: [
      {
        path: '',
        name: 'public-reseller-sales',
        component: () => import('@/views/public/ResellerSales.vue'),
        meta: {
          title: 'Tornar-se Revendedor - Veltrix Panel',
          public: true
        }
      }
    ]
  },
  {
    path: '/renew',
    component: MinimalLayout,
    meta: { public: true },
    children: [
      {
        path: '',
        name: 'public-renewal',
        component: () => import('@/views/public/Renewal.vue'),
        meta: {
          title: 'Renovar Acesso - Veltrix Panel',
          public: true
        }
      }
    ]
  },
  // Legacy PT-BR paths → English (keep shared links working)
  {
    path: '/vendas/:slug',
    redirect: (to) => ({ path: `/sales/${to.params.slug}`, query: to.query }),
  },
  {
    path: '/revendas/:slug',
    redirect: (to) => ({ path: `/reseller-sales/${to.params.slug}`, query: to.query }),
  },
  {
    path: '/renovar',
    redirect: (to) => ({ path: '/renew', query: to.query }),
  },

  // Auth routes
  {
    path: '/auth',
    component: AuthLayout,
    meta: { 
      requiresGuest: true,
      transition: 'slide' 
    },
    children: [
      {
        path: 'login',
        name: 'login',
        component: Login,
        meta: { title: 'Login - Veltrix Panel' }
      },
      {
        path: '',
        redirect: '/auth/login'
      }
    ]
  },

  // Dashboard routes
  {
    path: '/',
    component: DashboardLayout,
    meta: { 
      requiresAuth: true 
    },
    children: [
      {
        path: '',
        name: 'dashboard',
        component: Dashboard,
        meta: { 
          title: 'Dashboard - Veltrix Panel',
          transition: 'fade'
        }
      },
      {
        path: 'profile',
        name: 'profile',
        component: Profile,
        meta: { 
          title: 'Meu Perfil - Veltrix Panel',
          breadcrumb: [
            { name: 'Painel', path: '/' },
            { name: 'Perfil' }
          ]
        }
      },
      {
        path: 'automations',
        name: 'automations',
        component: () => import('@/views/automations/Automations.vue'),
        meta: { 
          title: 'Automações - Veltrix Panel',
          breadcrumb: [
            { name: 'Painel', path: '/' },
            { name: 'Automações' },
            { name: 'Gerenciar' }
          ]
        }
      },
      {
        path: 'accounts',
        name: 'accounts',
        component: () => import('@/views/accounts/Accounts.vue'),
        meta: { 
          title: 'Gerenciar Contas - Veltrix Panel',
          breadcrumb: [
            { name: 'Painel', path: '/' },
            { name: 'Contas' },
            { name: 'Gerenciar' }
          ]
        }
      },
      {
        path: 'account-plans',
        name: 'account-plans',
        component: () => import('@/views/accounts/AccountPlans.vue'),
        meta: { 
          title: 'Planos de Contas - Veltrix Panel',
          breadcrumb: [
            { name: 'Painel', path: '/' },
            { name: 'Contas' },
            { name: 'Planos' }
          ],
          permission: 'plans.view' // Admin, Super Admin e Revendedor
        }
      },
      {
        path: 'servers',
        name: 'servers',
        component: Servers,
        meta: { 
          title: 'Servidores - Veltrix Panel',
          breadcrumb: [
            { name: 'Painel', path: '/' },
            { name: 'Servers' },
            { name: 'Gerenciar' }
          ]
        }
      },
      // Rota 'users' removida - não é usada (usar /accounts para contas SSH/V2Ray)
      {
        path: 'categories',
        name: 'categories',
        component: Categories,
        meta: { 
          title: 'Categorias - Veltrix Panel',
          breadcrumb: [
            { name: 'Painel', path: '/' },
            { name: 'Servers' },
            { name: 'Categorias' }
          ],
          permissions: ['admin']
        }
      },
      {
        path: 'resellers',
        name: 'resellers',
        component: Resellers,
        meta: { 
          title: 'Revendedores - Veltrix Panel',
          breadcrumb: [
            { name: 'Painel', path: '/' },
            { name: 'Revendedores', path: '/resellers' },
            { name: 'Gerenciar' }
          ],
          permission: 'resellers.view' // Admin E Revendedor
        }
      },
      {
        path: 'reseller-plans',
        name: 'reseller-plans',
        component: () => import('@/views/reseller-management/ResellerPlans.vue'),
        meta: { 
          title: 'Planos de Revenda - Veltrix Panel',
          breadcrumb: [
            { name: 'Painel', path: '/' },
            { name: 'Revendedores' },
            { name: 'Planos' }
          ],
          permission: 'resellers.view' // Admin E Revendedor
        }
      },
      {
        path: 'reseller-auto-sales',
        name: 'reseller-auto-sales',
        component: () => import('@/views/reseller-management/ResellerAutoSales.vue'),
        meta: { 
          title: 'Revenda Automática - Veltrix Panel',
          breadcrumb: [
            { name: 'Painel', path: '/' },
            { name: 'Revendedores' },
            { name: 'Revenda Automática' }
          ],
          permissions: ['admin']
        }
      },
      {
        path: 'admin-settings',
        name: 'admin-settings',
        component: () => import('@/views/settings/AdminSettings.vue'),
        meta: { 
          title: 'Configurações do Sistema - Veltrix Panel',
          breadcrumb: [
            { name: 'Painel', path: '/' },
            { name: 'Sistema' },
            { name: 'Configurações' }
          ],
          superAdminOnly: true
        }
      },
      {
        path: 'admin-management',
        name: 'admin-management',
        component: () => import('@/views/admin-management/AdminManagement.vue'),
        meta: { 
          title: 'Gerenciamento de Administradores - Veltrix Panel',
          breadcrumb: [
            { name: 'Painel', path: '/' },
            { name: 'Sistema' },
            { name: 'Administradores' }
          ],
          superAdminOnly: true
        }
      },
      {
        path: 'logs',
        name: 'system-logs',
        component: SystemLogs,
        meta: { 
          title: 'Logs do Sistema - Veltrix Panel',
          breadcrumb: [
            { name: 'Painel', path: '/' },
            { name: 'Sistema' },
            { name: 'Logs' }
          ],
          superAdminOnly: true
        }
      },
      {
        path: 'checkuser',
        name: 'checkuser',
        component: CheckUser,
        meta: { 
          title: 'CheckUser DTunnel - Veltrix Panel',
          breadcrumb: [
            { name: 'Painel', path: '/' },
            { name: 'Sistema' },
            { name: 'CheckUser' }
          ],
          superAdminOnly: true
        }
      },
      {
        path: 'coupons',
        name: 'coupons',
        component: () => import('@/views/coupons/Coupons.vue'),
        meta: { 
          title: 'Cupons de Desconto - Veltrix Panel',
          breadcrumb: [
            { name: 'Painel', path: '/' },
            { name: 'Cupons' }
          ]
        }
      },
      {
        path: 'sales-metrics',
        name: 'sales-metrics',
        component: () => import('@/views/payments/SalesMetrics.vue'),
        meta: { 
          title: 'Métricas de Vendas - Veltrix Panel',
          breadcrumb: [
            { name: 'Painel', path: '/' },
            { name: 'Pagamentos' },
            { name: 'Métricas' }
          ]
        }
      },
      {
        path: 'renew-subscription',
        name: 'renew-subscription',
        component: () => import('@/views/reseller-management/RenewSubscription.vue'),
        meta: { 
          title: 'Renovar Assinatura - Veltrix Panel',
          breadcrumb: [
            { name: 'Painel', path: '/' },
            { name: 'Renovar Assinatura' }
          ]
        }
      },
      {
        path: 'buy-credits',
        name: 'buy-credits',
        component: () => import('@/views/reseller-management/BuyCredits.vue'),
        meta: { 
          title: 'Comprar Créditos - Veltrix Panel',
          breadcrumb: [
            { name: 'Painel', path: '/' },
            { name: 'Comprar Créditos' }
          ]
        }
      }
    ]
  },

  // Catch-all route for 404
  {
    path: '/:pathMatch(.*)*',
    component: MinimalLayout,
    children: [
      {
        path: '',
        name: 'not-found',
        component: NotFound,
        meta: {
          title: '404 - Page Not Found',
          public: true,
        }
      }
    ]
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
})

// Contador para prevenir loops infinitos
let redirectCount = 0
let lastRedirectPath = null

// Retorna o nome do painel a partir do cache de branding (sem import reativo)
function getCachedPanelName() {
  try {
    const cached = localStorage.getItem(STORAGE_KEYS.BRANDING)
    if (cached) {
      const data = JSON.parse(cached)
      if (data.branding?.pwa_short_name) return data.branding.pwa_short_name
      if (data.branding?.panel_name) return data.branding.panel_name
    }
  } catch { /* fallback */ }
  return 'Veltrix'
}

// ✅ Cache do install check para evitar request a cada navegação
let installChecked = false
let isInstalled = true

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  // Set page title (substitui 'Veltrix' pelo nome configurado)
  const pn = getCachedPanelName()
  const rawTitle = to.meta.title || ''
  document.title = rawTitle ? rawTitle.replace(/Veltrix Panel|Veltrix/g, pn) : (pn.includes('Panel') ? pn : pn + ' Panel')
  
  // ── Install check: apenas uma vez por sessão ──
  // Não bloquear login/licença/vendas: to.meta só reflecte a folha — usar matched + requiresGuest
  const skipInstallProbe =
    to.name === 'install' ||
    to.meta.public === true ||
    to.matched.some((r) => r.meta.public === true) ||
    to.matched.some((r) => r.meta.requiresGuest === true)

  if (!installChecked && !skipInstallProbe) {
    try {
      const installRes = await apiService.install.check()
      installChecked = true
      isInstalled = installRes?.installed !== false
      if (!isInstalled) {
        return next('/install')
      }
    } catch {
      installChecked = true // API unreachable — assume installed
    }
  } else if (installChecked && !isInstalled && to.name !== 'install') {
    return next('/install')
  }

  // Reset contador se mudou de rota (não é redirecionamento)
  if (to.path !== lastRedirectPath) {
    redirectCount = 0
  }
  
  // Proteção contra loop infinito - máximo 3 redirecionamentos
  if (redirectCount > 3) {
    console.error('❌ [Router] Loop infinito detectado! Abortando navegação.')
    redirectCount = 0
    lastRedirectPath = null
    // Força ir para login limpo
    if (to.path !== '/auth/login') {
      authStore.logout()
      window.location.href = resolveAppPath('auth/login')
    }
    return next(false)
  }
  
  // Verificar se precisa de autenticação
  if (to.meta.requiresAuth) {
    // Se não está autenticado (computed), verifica auth primeiro
    // Mas se tem token no localStorage, pode estar apenas dessincronizado
    if (!authStore.isAuthenticated) {
      // Se tem token no localStorage mas não no store, tenta sincronizar
      const tokenFromStorage = getAccessToken()
      const userFromStorage = localStorage.getItem(STORAGE_KEYS.USER)
      
      if (tokenFromStorage && !authStore.token) {
        authStore.token = tokenFromStorage
        if (userFromStorage) {
          try {
            authStore.user = JSON.parse(userFromStorage)
          } catch (e) {
            console.error('Erro ao parsear user do localStorage:', e)
          }
        }
      }
      
      // Se ainda não está autenticado após sincronização, verifica no servidor
      if (!authStore.isAuthenticated) {
        const isAuth = await authStore.checkAuth()
        
        if (!isAuth) {
          authStore.clearAuthData()
          redirectCount++
          lastRedirectPath = '/auth/login'
          next('/auth/login')
          return
        }
      }
    }
    
    // Revendedores usam mesma dashboard com permissões filtradas
    // Sem redirecionamento especial - todos usam /dashboard
    
    // Impedir não-revendedores de acessar reseller-portal
    if (to.meta.authType === 'reseller' && !authStore.isReseller) {
      next('/')
      return
    }
    
    // Impedir revendedores de acessar painel admin
    if (to.meta.resellerOnly && !authStore.isReseller) {
      next('/')
      return
    }
    
    // Sistema de permissões granulares (novo sistema baseado em permissions.js)
    if (to.meta.permission) {
      // Importar função de verificação de permissão
      const { DEFAULT_PERMISSIONS_BY_ROLE } = await import('@/config/permissions')
      const userRole = authStore.user?.role || authStore.user?.access
      const userPermissions = DEFAULT_PERMISSIONS_BY_ROLE[userRole] || []
      
      // Super admin tem tudo
      if (!userPermissions.includes('*')) {
        // Verificar permissão específica
        const hasPermission = userPermissions.includes(to.meta.permission) || 
                              userPermissions.includes(to.meta.permission.split('.')[0] + '.*')
        
        if (!hasPermission) {
          console.warn(`⛔ Acesso negado: rota requer permissão '${to.meta.permission}'`)
          next('/')
          return
        }
      }
    }
    
    // Sistema legado de permissões (compatibilidade)
    if (to.meta.permissions) {
      const userAccess = authStore.user?.access || authStore.user?.role
      const userRole = authStore.user?.role || authStore.user?.access
      
      // Super admin tem acesso a tudo
      if (userRole !== 'super_admin' && !to.meta.permissions.includes(userAccess)) {
        next('/')
        return
      }
    }
    
    // Impedir não-super-admins de acessar rotas exclusivas de super admin
    if (to.meta.superAdminOnly) {
      const userRole = authStore.user?.role || authStore.user?.access
      
      if (userRole !== 'super_admin') {
        next('/')
        return
      }
    }

    // ── Subscribe expiry guard ──
    // Revendedor subscribe expirado só pode acessar /renew-subscription e /profile
    const userRole = authStore.user?.role || authStore.user?.access
    if ((userRole === 'reseller' || userRole === 'sub_reseller') && authStore.user?._reseller_expired) {
      const allowedPaths = ['/renew-subscription', '/profile']
      if (!allowedPaths.includes(to.path)) {
        next('/renew-subscription')
        return
      }
    }
  }
  
  // Redirecionar usuários autenticados das páginas de guest
  if (to.meta.requiresGuest && authStore.isAuthenticated) {
    redirectCount++
    lastRedirectPath = '/'
    next('/')
    return
  }
  
  // Verificar permissões específicas
  if (to.meta.adminOnly && !authStore.isAdmin) {
    next('/')
    return
  }
  
  next()
})

// After navigation
router.afterEach((to, from) => {
  // You can add analytics tracking here
  // gtag('config', 'GA_MEASUREMENT_ID', {
  //   page_path: to.path,
  // })
})

export default router